/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import PropTypes from 'prop-types';
import mbpLogger from 'mbp-logger';
import { connect, useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router';

// MUI
import { Button } from '@material-ui/core';

// APIs
import { updateEmail } from '../../../../../apis/account-apis/updateEmail';
import { updateEmailSubscribeStatus } from '../../../../../apis/account-apis/updateEmailSubscribeStatus';
import { fetchEmailSubscribeStatus } from '../../../../../apis/account-apis/fetchEmailSubscribeStatus';

// Helper
import Helper from '../../../Account/state/reducers/Helper';

import { withStyles } from '@material-ui/core/styles';
import { compose } from 'recompose';
import withPageView from '../../../../helpers/tracking/hocs/withPageView';
import { form as FormStyle } from '../../style/form';
import configENV from '../../config';
import { trackPage } from '../../../../helpers/tracking/common/commonTrackingHelpers';
import GraphqlSeoHeadTags from '../../../../components/GraphqlComponents/GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
import AccountHome from '../MyAccount/AccountHome';
import getSiteConfig from '../../utils/getSiteConfig';
import brandtheme from '../../../../components/AppShell/brandtheme';
import { schema } from './schema';
import { updateEmailAddress } from '../../../../../state/ducks/Member/ducks/Profile/Profile-Actions';
import EventDispatcher from '../../state/reducers/EventDispatcher';
import { commonUseStyles } from '../../style/commonStyles';
import { useAuth } from '../../../../../state/ducks/Member/ducks/Auth/Plugin/auth';
import { getFeatureFlag } from '../../../../../state/ducks/Checkout/ducks/Global/Global-Selectors';
import { getProfileInfo } from '../../../../../state/ducks/Member/ducks/Common/Common-Selectors';


const { PERSIST_KEY } = configENV;
const styles = ({ typography, palette }) => FormStyle(typography, palette);


const ChangeEmail = ({ brandId, classes, isReadyState }) => {
    const [error, setError] = useState('');
    const { email, ContactId } = useSelector((state) => state.account.userProfile || state.member.profile);
    const customClasses = commonUseStyles();
    const postEmailChangeAuthAction = useSelector(getFeatureFlag('post-email-change-auth-action-variant'));
    const userProfile = useSelector(getProfileInfo);

    const brandsEmailOpt = {
        FB: '18B',
        FBQ: '18B',
        HD: 'HND',
        WF: 'WLF',
        BRY: 'BERRIES',
        VC: 'VTC',
        CSG: 'CL',
    };

    const brand = brandsEmailOpt[brandId] || '18F';

    const [currentEmail, setCurrentEmail] = useState(email || '');
    const [isNewEmail, setIsNewEmail] = useState(false);
    const [showLabel, setShowLabel] = useState({
        newEmail: false,
        confirmNewEmail: false,
    });

    const {
        register, handleSubmit, formState: {
            errors, isValid,
        }, watch, reset,
    } = useForm({ mode: 'all', resolver: joiResolver(schema) });

    const watchNewEmail = watch('newEmail');
    const watchConfirmNewEmail = watch('confirmNewEmail');

    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { loginByRedirect, getAccessTokenSafely, logout } = useAuth();

    useEffect(() => {
        setError('');
    }, [watchNewEmail, watchConfirmNewEmail]);


    // fetch email subscription status
    const fetchEmailSubscribeResponse = useQuery({
        queryKey: 'fetchEmailSubscribeStatus',
        queryFn: () => fetchEmailSubscribeStatus({
            retrieveEmailRequest: 
                {
                    email: userProfile.email,
                    siteId: brand,
                    sourceSystem: "SSP",
                }
        }),
        staleTime: 'Infinity',
        refetchOnWindowFocus: false,
    });

    const updateEmailSubscribeStatusMutation = useMutation({
        mutationFn: updateEmailSubscribeStatus,
        onSuccess: () => queryClient.invalidateQueries(['fetchEmailSubscribeStatus'])
    })

    const updateEmailPreferences = async (confirmNewEmail) => {
        if (confirmNewEmail) {
            // Get previous email preference status
            const isUserSubscribedToMarketingEmails = fetchEmailSubscribeResponse.data?.data?.retrieveEmailResponse?.retrieveEmailResponseResult?.retrieveEmailResult?.retrieveEmailMessage === 'Active';
            const object = {
                userProfile: {
                    email: confirmNewEmail,
                    timestamp: new Date().getTime(),
                },
            };
            // Store new userProfile
            Helper.setSessionStorageObject(ContactId, object);

            if (isUserSubscribedToMarketingEmails) {

                // Unsubscribe previous email address
                await updateEmailSubscribeStatusMutation.mutate({
                    brand: brandId,
                    email,
                    action: 'DELETE',
                });
                setTimeout(() => {
                    // Subscribe new email address
                    updateEmailSubscribeStatusMutation.mutate({
                        brand: brandId,
                        email: confirmNewEmail,
                        action: 'SUBSCRIBE',
                    })
                }, 3000);
            }
        }
    };

    const updateEmailAddressMutation = useMutation({
        mutationFn: updateEmail,
        onSuccess: async (data) => {
            if (data.status === 200 && data && data.data !== undefined) {
                const confirmNewEmail = watch('confirmNewEmail');
                setCurrentEmail(watch('confirmNewEmail'));
                dispatch(updateEmailAddress(confirmNewEmail));
                dispatch(EventDispatcher.updateUserProfile({ email: confirmNewEmail }));
                setIsNewEmail(true);
                updateEmailPreferences(confirmNewEmail);
                reset({
                    newEmail: '',
                    confirmNewEmail: '',
                });

            if (postEmailChangeAuthAction === 'logout') {
                    await logout();
                } else if (postEmailChangeAuthAction === 'redirect') {
                    //Force session invalidation check and redirect to sign-in with prefill
                    await getAccessTokenSafely();
                    await loginByRedirect({
                        login_hint: confirmNewEmail,
                    },
                    {
                        routeBack: '/',
                    });
                }
            }
        },
        onError: (err) => {
            if (err.response.data.message) {
                setError(err.response.data.message);
            } else {
                setError('Something went wrong. Please try again.');
            }
            mbpLogger.logError({
                function: 'updateEmailAddressMutation',
                appName: process.env.npm_package_name,
                module: 'ChangeEmail.js',
                message: `Error While Updating an email address`,
                err,
            });
        },
    });

    const changeEmailAddress = async (updateEmailPayload) => {
        updateEmailAddressMutation.mutate(updateEmailPayload);
    };

    const onSubmit = (data) => {
        const payload = {
            newEmail: data.confirmNewEmail,
        };
        changeEmailAddress(payload);
    };

    const newEmailTemplate = (currentEmail) => (
        <>
            <p>To change your Email, please enter new Email twice.</p>
            {!isNewEmail && <p className="margin-bottom-1x">Changing your email address will sign you out of your account. Please sign back in using your new email.</p>}
            <div className="margin-top-2x">
                <span className="bold">Current Email: </span>
                <span>{currentEmail}</span>
            </div>
        </>
    );

    const updatedEmailTempate = (currentEmail) => {
        const primaryBrandColor = brandtheme?.[brandId]?.palette?.colorPrimary || '';

        return (
            <>
                <p data-test="my-account-success-email-changed" style={{ color: primaryBrandColor }}>
                    Your email has been updated.
                </p>
                <div className="margin-top-2x margin-bottom-2x">
                    <span className="bold">New Email: </span>
                    <span data-test="my-account-updated-email">{currentEmail}</span>
                </div>
                <p>To change your Email, please enter new Email twice.</p>
            </>
        )
    };

    const changeEmailForm = (classes, isReadyState) => (
        <>
            <form
                className={`${classes.form} row margin-top-2x`}
            >
                <div className="label-style">
                    {showLabel.newEmail && <label htmlFor="newEmail" aria-label="please enter new email">New Email</label>}
                    <input type="hidden" {...register('currentEmail')} value={currentEmail} />
                    <input
                        id="newEmail"
                        type="email"
                        className={`input-border-bottom full-width padding-half ${errors?.newEmail?.message ? 'borderError' : ''}`}
                        placeholder={!showLabel.newEmail && 'New Email'}
                        onClick={() => setShowLabel({ ...showLabel, newEmail: true })} // for on click of the input field
                        onBlurCapture={() => setShowLabel({ ...showLabel, newEmail: false })} // using onBlurCapture to not override default onBlur error validation by RHF
                        onFocus={() => setShowLabel({ ...showLabel, newEmail: true })} // for while focus on input field using tab
                        {...register('newEmail')}
                    />
                    {errors?.newEmail?.message && <p className="error-color small no-margin-bottom">{errors?.newEmail?.message}</p>}
                </div>
                <div className="label-style">
                    {showLabel.confirmNewEmail && <label htmlFor="confirmNewEmail" aria-label="please enter confirmation new email">New Confirmation Email</label>}
                    <input
                        id="confirmNewEmail"
                        type="email"
                        className={`input-border-bottom full-width padding-half ${errors?.confirmNewEmail?.message ? 'borderError' : ''}`}
                        placeholder={!showLabel.confirmNewEmail && 'New Confirmation Email'}
                        onClick={() => setShowLabel({ ...showLabel, confirmNewEmail: true })} // for on click of the input field
                        onBlurCapture={() => setShowLabel({ ...showLabel, confirmNewEmail: false })} // using onBlurCapture to not override default onBlur error validation by RHF
                        onFocus={() => setShowLabel({ ...showLabel, confirmNewEmail: true })} // for while focus on input field using tab
                        {...register('confirmNewEmail')}
                    />
                    {errors?.confirmNewEmail?.message && <p className="error-color small no-margin-bottom">{errors?.confirmNewEmail?.message}</p>}
                </div>
                {error && <p className='error-color align-center margin-top'>{error}</p>}
                <Button
                    onClick={handleSubmit(onSubmit)}
                    // type='submit'
                    disabled={!isReadyState || !isValid || updateEmailAddressMutation.isLoading}
                    className={'buttonHD margin-top-2x opacity1'}
                >
                    {!updateEmailAddressMutation.isLoading ? 'Update Email' : 'Updating Email...'}
                </Button>
            </form>
        </>
    );

    return (
        <>
            <GraphqlSeoHeadTags seoData={{
                page_title: 'My Account',
                location: { pathname: '/account/change-email' },
            }}
            />
            <div className={customClasses.container}>
                <AccountHome disablePageView />
            </div>
            <div className={customClasses.orderContainer}>
                <h1>
                    Change Email
                </h1>
            </div>
            {!isNewEmail
                ? newEmailTemplate(currentEmail)
                : updatedEmailTempate(currentEmail)}
            {changeEmailForm(classes, isReadyState)}
        </>
    );
}


ChangeEmail.propTypes = {
    classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => {
    /* istanbul ignore next */
    const newState = state[PERSIST_KEY] ? state[PERSIST_KEY] : state;
    const brandId = getSiteConfig('brand-id') || '';
    return {
        isReadyState: newState.isReadyState,
        toggleState: newState.toggleState,
        error: newState.error,
        brandId,
    };
};

const enhance = compose(
    connect(
        mapStateToProps,
    ),
    trackPage({
        type: 'changeemail',
        name: 'MyAccount',
        accountPage: true,
    }),
    withRouter,
    withStyles(styles),
    withPageView({
        pageViewSuccessTest: () => true,
    }),
);
export default enhance(ChangeEmail);
